/**
 * The cross-feature contract. Features never import each other's internals;
 * a feature PROVIDES members of SimBridge (UI) or SimServices (mock side) in
 * its manifest, and others look them up here. A lookup returns undefined when
 * the providing feature is off or not loaded — callers must degrade (hide the
 * placement, offer "Save checklist" instead of "Add to cart", …).
 *
 * Keys are "<providerFeatureId>.<name>". Adding a key is a contract change:
 * add it here first, then implement it in the provider's manifest.
 */
import { useSyncExternalStore, type ComponentType } from "react";
import type { AccentName } from "../../lib/accents";
import { isFeatureEnabled, type SimFeatureId } from "./flags";
import { getLoadedManifest, getRegistryVersion, subscribeRegistry } from "./registry";
import { useSimStore } from "./store";
import type { SimScenario } from "./types";

/* ---------- shared shapes ---------- */

/** What a product looked like when it was shown (the cart keeps this snapshot). */
export interface ProductSnapshot {
  productId: string;
  name: string;
  /** Whole pesos (₱). */
  price: number;
  /** Partner store, e.g. "Bayanihan Mart". */
  store: string;
  /** e.g. "rain-gear", "snacks", "first-aid". Banned: see AdPolicyApi.isBannedCategory. */
  category: string;
  /** lucide-react icon name hint for the illustrated tile, e.g. "Umbrella". */
  icon?: string;
  accent?: AccentName;
}

export interface CartItemInput extends ProductSnapshot {
  qty: number;
  /** One-line "why recommended" shown in the cart (e.g. "Theo is 3 → size S"). */
  reason?: string;
}

export interface AddToCartOptions {
  bundleId?: string;
  bundleName?: string;
  /** Where the add came from, for partner analytics (e.g. "seasonal.typhoon-go-bag"). */
  source?: string;
}

export type AdSurface =
  | "dashboard"
  | "communityFeed"
  | "communitySidebar"
  | "storyEnd"
  | "stories"
  | "seasonalBundle"
  | "weeklyChallenge"
  | "shop"
  | "landing"
  | "partners";

export interface PlacementContext {
  surface: AdSurface;
  /** Stable id for frequency caps and analytics, e.g. "dashboard.footer.partner". */
  placementId: string;
  /** Topic/category labels of what's around the placement ("Mental health" blocks). */
  topics?: readonly string[];
  /** Product category, when the placement shows a product. */
  category?: string;
  /** 0-based position in a feed, when the placement sits in one. */
  feedIndex?: number;
}

/** shop/adPolicy.ts — every sponsored or product placement asks this first. */
export interface AdPolicyApi {
  canShow(ctx: PlacementContext): boolean;
  /** Feed indexes after which a sponsored unit may go: never in the first 3, at most 1 per 8. */
  feedPositions(organicCount: number): number[];
  /** "Not for me": hides the placement and frequency-caps it. */
  dismiss(placementId: string): void;
  isBannedCategory(category: string): boolean;
  /** The "Personalized recommendations" setting (/settings/recommendations). */
  isPersonalized(): boolean;
}

export interface SponsoredLabelProps {
  placement: PlacementContext;
  label?: "Sponsored" | "Partner";
  /** Plain-language reason for the "Why am I seeing this?" popover. */
  why: string;
  /** Called after "Not for me" (the policy has already recorded it). */
  onDismiss?: () => void;
  className?: string;
}

export interface SeasonalAlertBrief {
  id: string;
  title: string;
  urgency: "info" | "prepare" | "urgent";
  scenario: SimScenario;
  bundleId?: string;
}

/** A task another feature contributes to Daily wins. */
export interface WinTaskSeed {
  id: string;
  title: string;
  minutes?: number;
  /** In-app link the task opens. */
  href?: string;
  points: number;
  source: "profile" | "age" | "seasonal" | "community" | "selfCare";
}

/* ---------- the contract ---------- */

export interface SimBridge {
  /** One call, one toast, opens the cart drawer showing the bundle as a group. */
  "shop.addItemsToCart": (items: CartItemInput[], opts?: AddToCartOptions) => Promise<void>;
  "shop.openCart": () => void;
  "shop.adPolicy": AdPolicyApi;
  /** "Sponsored"/"Partner" pill + "Why am I seeing this?" popover + "Not for me". */
  "shop.SponsoredLabel": ComponentType<SponsoredLabelProps>;
  /** A native product card for feeds (labels itself, checks the policy itself). */
  "shop.SponsoredFeedUnit": ComponentType<{ placement: PlacementContext; className?: string }>;
  /** "Picked for this topic" shelf (story endings); renders nothing when the policy says no. */
  "shop.ProductShelf": ComponentType<{
    topic: string;
    title?: string;
    placement: PlacementContext;
    className?: string;
  }>;
  /** Verified hotlines and gentle words; never shown next to ads. */
  "safeSpace.SupportResourcesCard": ComponentType<{ compact?: boolean; className?: string }>;
  /** "Presented by <partner>" ribbon for a placement; renders nothing if unsponsored or blocked. */
  "partners.PresentedBy": ComponentType<{ placementId: string; className?: string }>;
  /** Re-open the first-login welcome sheet (Sim Panel "Replay first login", help button). */
  "welcome.replay": () => void;
}

export interface SimServices {
  "shop.bundleHistory": () => { inCart: string[]; ordered: string[] };
  "seasonal.activeAlerts": () => SeasonalAlertBrief[];
  /** Seasonal tasks for today, e.g. "Pack the go-bag" once the bundle is in the cart. */
  "seasonal.tasks": () => WinTaskSeed[];
  "community.memberStats": () => { introduced: boolean; circlesJoined: number; posts: number };
  "stories.readerStats": () => { read: number; thanked: number; submitted: number };
  "directory.followed": () => { slug: string; name: string }[];
  "partners.placementSponsor": (placementId: string) => { partnerId: string; name: string } | null;
}

/* ---------- lookups ---------- */

function providerOf(key: string): SimFeatureId {
  return key.slice(0, key.indexOf(".")) as SimFeatureId;
}

export function getBridge<K extends keyof SimBridge>(key: K): SimBridge[K] | undefined {
  const provider = providerOf(key);
  if (!isFeatureEnabled(provider)) return undefined;
  return getLoadedManifest(provider)?.provides?.[key] as SimBridge[K] | undefined;
}

/** Reactive lookup: re-renders when features load or are toggled in the Sim Panel. */
export function useBridge<K extends keyof SimBridge>(key: K): SimBridge[K] | undefined {
  useSyncExternalStore(subscribeRegistry, getRegistryVersion, getRegistryVersion);
  useSimStore((s) => s.featureOverrides);
  return getBridge(key);
}

type ServiceArgs<K extends keyof SimServices> = SimServices[K] extends (...args: infer A) => unknown
  ? A
  : never;
type ServiceResult<K extends keyof SimServices> = SimServices[K] extends (
  ...args: never[]
) => infer R
  ? R
  : never;

/** Mock-side call into another feature; undefined when that feature is off. */
export function callService<K extends keyof SimServices>(
  key: K,
  ...args: ServiceArgs<K>
): ServiceResult<K> | undefined {
  const provider = providerOf(key);
  if (!isFeatureEnabled(provider)) return undefined;
  const fn = getLoadedManifest(provider)?.services?.[key] as
    | ((...a: ServiceArgs<K>) => ServiceResult<K>)
    | undefined;
  return fn ? fn(...args) : undefined;
}
